import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { MdKeyboardArrowUp, MdInfoOutline } from 'react-icons/md'

import type { ReactNode, ReactElement } from 'react'

import Badge from '~/components/Badge'
import Label from '~/components/Label'

import './styles.scss'

export interface Tab {
  component?: ReactNode
  icon?: ReactElement
  isChecked?: boolean
  isError?: boolean
  itemsCount?: number
  label: ReactNode | string
  labelClassName?: string
  name: string
  subTabs?: Tab[]
  tabId?: string
}

export interface Props {
  className?: string
  currentSubTab?: string
  currentTab: string
  filter?: string
  onClickTab?: (tabName: string) => void
  onSubTabClick?: (subTabName: string) => void
  tabsData: Tab[]
  variant?: 'primary' | 'secondary'
}

const VerticalTabs = ({
  className,
  currentSubTab,
  currentTab,
  onClickTab,
  onSubTabClick,
  tabsData,
  variant = 'primary',
}: Props): ReactElement => {
  const tabulationClasses = classNames('vertical-tabs', className, variant)

  const [activeTabName, setActiveTabName] = useState(tabsData[0]?.name)

  const TabComponent = tabsData.find(({ name }) => activeTabName === name)?.component

  const handleSubTabClick = (name: string) => {
    onSubTabClick && onSubTabClick(name)
  }

  const handleTabClick = (tabName: string) => {
    setActiveTabName(tabName)
    onClickTab && onClickTab(tabName)
  }

  useEffect(() => {
    if (currentTab && currentTab !== activeTabName) {
      setActiveTabName(currentTab)
    }
  }, [activeTabName, currentTab])

  return (
    <div className="vertical-tabs-container">
      <div className={tabulationClasses}>
        {tabsData.map(({ icon, isChecked, itemsCount, label, labelClassName, name, subTabs, tabId }, index) => (
          <div id={tabId} key={`tab-${index}`}>
            <div
              className={classNames('vertical-tab', {
                active: name === activeTabName,
                collapsed: name !== activeTabName,
              })}
              onClick={() => handleTabClick(name)}
            >
              <div className={classNames('label-container', labelClassName)}>
                {icon ? icon : null}
                {typeof label === 'string' ? <Label className="vertical-tab__label" label={label} /> : label}
                {!isChecked && itemsCount !== undefined ? (
                  <Badge className="item-count-badge" value={itemsCount} />
                ) : null}
                {isChecked ? <FaCheckCircle className="check-icon" /> : null}
              </div>
              {subTabs ? <MdKeyboardArrowUp className="arrow-icon" /> : null}
            </div>
            {!!subTabs && name === activeTabName ? (
              <div className="sub-tabs-container">
                {subTabs.map(({ icon, isChecked, isError, label, name }) => (
                  <div
                    className={classNames('sub-tab', { active: name === currentSubTab })}
                    key={`sub-tab-${name}`}
                    onClick={() => handleSubTabClick(name)}
                  >
                    <div className={classNames('label-container', labelClassName)}>
                      {icon ? icon : null}
                      {typeof label === 'string' ? <Label className="vertical-tab__label" label={label} /> : label}
                      {isChecked ? <FaCheckCircle className="check-icon" /> : null}
                      {isError ? <MdInfoOutline color="#ca3423" size={16} /> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {TabComponent}
    </div>
  )
}

export default VerticalTabs
